import { type ContractorDto, type CreateContractorRequest, type UpdateContractorRequest, contractorsApi } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { icon } from '../../icons';
import { t } from '../../services/i18n';

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function openContractorCreateDialog(onCreated: () => Promise<void>): void {
  openContractorDialog(null, onCreated);
}

export function openContractorEditDialog(contractor: ContractorDto, onUpdated: () => Promise<void>): void {
  openContractorDialog(contractor, onUpdated);
}

function openContractorDialog(contractor: ContractorDto | null, onDone: () => Promise<void>): void {
  const isEdit = contractor !== null;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>${isEdit ? t('contractor.edit') : t('contractor.new')}</h2>
        <button class="modal-close" data-action="cancel">${icon('x', { size: 20 })}</button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${t('common.name')} *</label>
          <input name="name" required placeholder="${t('contractor.namePlaceholder')}" value="${isEdit ? esc(contractor!.name) : ''}">
        </div>
        <div class="form-group">
          <label>${t('contractor.trade')}</label>
          <input name="trade" placeholder="${t('contractor.tradePlaceholder')}" value="${isEdit && contractor!.trade ? esc(contractor!.trade) : ''}">
        </div>
        <div class="form-group">
          <label>${t('common.phoneNumber')}</label>
          <input name="phoneNumber" type="tel" placeholder="${t('contractor.phoneNumberPlaceholder')}" value="${isEdit && contractor!.phoneNumber ? esc(contractor!.phoneNumber) : ''}">
        </div>
        <div class="form-group">
          <label>${t('common.email')}</label>
          <input name="email" type="email" placeholder="${t('contractor.emailPlaceholder')}" value="${isEdit && contractor!.email ? esc(contractor!.email) : ''}">
        </div>
        <div class="form-group">
          <label>${t('common.notes')}</label>
          <textarea name="notes" rows="3" placeholder="${t('contractor.notesPlaceholder')}">${isEdit && contractor!.notes ? esc(contractor!.notes) : ''}</textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${isEdit ? t('common.save') : t('common.add')}</button>
        </div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    submitBtn.disabled = true;
    submitBtn.textContent = isEdit ? t('common.saving') : t('common.processing');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    const fd = new FormData(event.currentTarget as HTMLFormElement);
    const data: CreateContractorRequest | UpdateContractorRequest = {
      name: fd.get('name') as string,
      trade: (fd.get('trade') as string) || undefined,
      phoneNumber: (fd.get('phoneNumber') as string) || undefined,
      email: (fd.get('email') as string) || undefined,
      notes: (fd.get('notes') as string) || undefined,
    };
    try {
      if (isEdit) {
        await contractorsApi.update(contractor!.id, data as UpdateContractorRequest);
      } else {
        await contractorsApi.create(data as CreateContractorRequest);
      }
      close();
      await onDone();
    } catch (error) {
      console.error('Failed to save contractor:', error);
      const message = error instanceof ApiError ? error.message : t('common.errorGeneric');
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = message;
      }
      submitBtn.disabled = false;
      submitBtn.textContent = isEdit ? t('common.save') : t('common.add');
    }
  });
  document.body.appendChild(overlay);
}
